import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../../utils/api';
import { getActiveTaskTimerEventName, readActiveTaskTimer, writeActiveTaskTimer } from '../../modules/projectManagement/taskTimerStorage';
import { REFERENCE_DATA_AUTH_EVENT } from '../../contexts/ReferenceDataContext';
import { clearAuthStorage } from '../../utils/auth';

const Topbar = ({ userRole }) => {
  const navigate = useNavigate();
  const user = JSON.parse(sessionStorage.getItem('user') || '{}');
  const [showUserMenu, setShowUserMenu] = useState(false);
  const menuRef = useRef(null);

  const [notificationCount, setNotificationCount] = useState(0);
  const [loadingNotifications, setLoadingNotifications] = useState(false);
  const [activeTaskTimer, setActiveTaskTimer] = useState(() => readActiveTaskTimer());
  const [timerNow, setTimerNow] = useState(Date.now());

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (menuRef.current && !menuRef.current.contains(event.target)) {
        setShowUserMenu(false);
      }
    };

    if (showUserMenu) {
      document.addEventListener('mousedown', handleClickOutside);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [showUserMenu]);

  // Fetch notification count from backend
  useEffect(() => {
    const fetchNotificationCount = async () => {
      try {
        setLoadingNotifications(true);

        // Fetch unread notification count from backend
        const response = await api.getNotificationCount();

          if (response.success) {
          setNotificationCount(response.count || 0);
        }
      } catch (error) {
        console.error('Error fetching notification count:', error);
        setNotificationCount(0);
      } finally {
        setLoadingNotifications(false);
      }
    };

    fetchNotificationCount();

    // Listen for global notification updates (from notifications page)
    const handleNotificationsUpdated = () => {
      fetchNotificationCount();
    };

    window.addEventListener('notificationsUpdated', handleNotificationsUpdated);

    return () => {
      window.removeEventListener('notificationsUpdated', handleNotificationsUpdated);
    };
  }, [user._id, user.id]);

  useEffect(() => {
    const eventName = getActiveTaskTimerEventName();
    const refreshTaskTimer = () => {
      setActiveTaskTimer(readActiveTaskTimer());
    };

    refreshTaskTimer();
    window.addEventListener(eventName, refreshTaskTimer);
    window.addEventListener('storage', refreshTaskTimer);

    return () => {
      window.removeEventListener(eventName, refreshTaskTimer);
      window.removeEventListener('storage', refreshTaskTimer);
    };
  }, []);

  useEffect(() => {
    if (!activeTaskTimer?.isRunning) return undefined;
    const intervalId = window.setInterval(() => setTimerNow(Date.now()), 1000);
    return () => window.clearInterval(intervalId);
  }, [activeTaskTimer?.isRunning]);

  const handleLogout = async () => {
    try {
      await api.logout();
    } catch (error) {
      console.error('Logout error:', error);
    } finally {
      // Drops the stored user plus the saved list filters/tabs, so the next sign-in starts clean.
      clearAuthStorage();
      window.dispatchEvent(new Event(REFERENCE_DATA_AUTH_EVENT));
      navigate('/login');
    }
  };

  const formatElapsedTime = (durationMs) => {
    const safeSeconds = Math.max(0, Math.floor(Number(durationMs || 0) / 1000));
    const hours = String(Math.floor(safeSeconds / 3600)).padStart(2, '0');
    const minutes = String(Math.floor((safeSeconds % 3600) / 60)).padStart(2, '0');
    const seconds = String(safeSeconds % 60).padStart(2, '0');
    return `${hours}:${minutes}:${seconds}`;
  };

  const getTimerElapsedMs = (timer) => {
    if (!timer) return 0;
    const base = Number(timer.elapsedMs || 0);
    if (!timer.isRunning) return base;
    const startedAt = Number(timer.startedAt || 0);
    if (!startedAt) return base;
    return base + Math.max(0, timerNow - startedAt);
  };

  const handlePauseTaskTimer = async () => {
    const timer = readActiveTaskTimer();
    if (!timer?.taskId) return;

    const elapsedMs = getTimerElapsedMs(timer);
    const deltaHour = elapsedMs / (1000 * 60 * 60);

    try {
      await api.updateTask(timer.taskId, {
        addActualHour: Number(deltaHour.toFixed(2)),
        actualHourScope: 'self',
        timerStopped: true,
        timerStartedAt: timer.startedAt || null,
        timerStoppedAt: Date.now(),
      });

      writeActiveTaskTimer(null);
    } catch (error) {
      console.error('Failed to pause task timer:', error);
    }
  };

  const getPageTitle = () => {
    const path = window.location.pathname;
    if (path.includes('dashboard')) return 'Dashboard';
    if (
      path.includes('teams-events') &&
      (path.includes('/feedback') || path.includes('view-feedback'))
    ) {
      return 'Team Feedback';
    }
    if (path.includes('copy-team')) return 'Copy Team';
    if (path.includes('edit-team')) return 'Edit Team';
    if (path.includes('add-team')) return 'Add Team';
    if (path.includes('teams-events')) return 'Teams';
    if(path.includes('matches')) return 'Matches'
    if (path.includes('projects')) return 'Projects';
    if (path.includes('tasks')) return 'Tasks';
    if (path.includes('checkout')) return 'Check-Out';
    if (path.includes('checkin')) return 'Check-In';
    if (path.includes('acknowledgements')) return 'Acknowledgements';
    if (path.includes('assets/add')) return 'Add Asset';
    // Admin asset detail page
    if (path.match(/\/admin\/assets\/[^/]+$/)) return 'Asset Details';
    if (path.includes('assets')) return 'Assets';
    if (path.includes('people/add')) return 'Add New Person / Employee';
    if (path.includes('people')) return 'People/Employees';
    if (path.includes('sites')) return 'Sites';
    if (path.includes('departments')) return 'Departments';
    // Reports - specific pages
    if (path.includes('reports/site-wise')) return 'Site Wise Report';
    if (path.includes('reports/category-wise')) return 'Category Wise Report';
    if (path.includes('reports/employee-history')) return 'Employee History Report';
    if (path.includes('reports/asset-history')) return 'Asset History Report';
    if (path.includes('employee-feedback-report')) return 'Employee Feedback Report';
    if (path.includes('team-feedback-report')) return 'Team Feedback Report';
    if (path.includes('performance-report')) return 'Performance Report';
    if (path.includes('reports')) return 'Reports';
    if (path.includes('settings/general')) return 'General Settings';
    if (path.includes('settings/user-roles')) return 'User Roles & Permissions';
    if (path.includes('settings/columns')) return 'Column Setup';
    if (path.includes('settings/job-types')) return 'Job Types';
    if (path.includes('settings/feedback-parameters')) return 'Feedback Parameters';
    if (path.includes('settings')) return 'Settings';
    if (path.includes('trash')) return 'Trash';
    // Employee handover detail page (has handoverId in path)
    if (path.match(/\/my-asset-handover\/[^/]+$/)) return 'Asset Details';
    if (path.includes('handover')) return 'Asset Handover';
    if (path.includes('profile')) return 'Profile';
    if (path.includes('notifications')) return 'Notifications';
    return 'Asset Management';
  };


  return (
    <div className="topbar">
      <div className="topbar-left">
        <div className="page-title">{getPageTitle()}</div>
      </div>
      
      <div className="topbar-right">
        <div className="topbar-actions">
          {activeTaskTimer?.taskId && (
            <div className={`task-timer-pill ${activeTaskTimer.isRunning ? 'running' : 'paused'}`}>
              <button
                type="button"
                className="task-timer-task"
                title={activeTaskTimer.taskName || 'Task timer'}
                onClick={() => navigate(`/${userRole}/project-management/tasks/${activeTaskTimer.taskId}`)}
              >
                ⏱ {activeTaskTimer.taskName || 'Task'}
              </button>
              <span className="task-timer-time">{formatElapsedTime(getTimerElapsedMs(activeTaskTimer))}</span>
              {activeTaskTimer.isRunning ? (
                <button
                  type="button"
                  className="task-timer-pause"
                  onClick={handlePauseTaskTimer}
                  title="Pause timer"
                >
                  ⏸
                </button>
              ) : (
                <span className="task-timer-paused-label">Paused</span>
              )}
            </div>
          )}
          <button
            className="notification-btn"
            title="Notifications"
            onClick={() => navigate(`/${userRole}/notifications`)}
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"></path>
              <path d="M13.73 21a2 2 0 0 1-3.46 0"></path>
            </svg>
            {!loadingNotifications && notificationCount > 0 && (
              <span className="notification-badge">
                {notificationCount}
              </span>
            )}
          </button>
        </div>

        <div className="user-menu-wrapper" ref={menuRef}>
          <div 
            className="user-menu-trigger"
            onClick={() => setShowUserMenu(!showUserMenu)}
          >
            <div className="user-avatar-small">
              {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
            </div>
            <div className="user-info-small">
              <div className="user-name-small">{user.name || 'User'}</div>
            </div>
            <svg 
              className={`dropdown-arrow ${showUserMenu ? 'open' : ''}`}
              width="16" 
              height="16" 
              viewBox="0 0 24 24" 
              fill="none" 
              stroke="currentColor" 
              strokeWidth="2"
            >
              <polyline points="6 9 12 15 18 9"></polyline>
            </svg>
          </div>

          {showUserMenu && (
            <div className="user-menu-dropdown">
              <div className="user-menu-header">
                <div className="user-avatar-menu">
                  {user.name ? user.name.charAt(0).toUpperCase() : 'U'}
                </div>
                <div className="user-details-menu">
                  <div className="user-name-menu">{user.name || 'User'}</div>
                  <div className="user-email-menu">{user.email || 'user@example.com'}</div>
                </div>
              </div>
              <div className="user-menu-divider"></div>
              <div className="user-menu-item" onClick={() => navigate(`/${userRole}/profile`)}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2"></path>
                  <circle cx="12" cy="7" r="4"></circle>
                </svg>
                <span>Profile Settings</span>
              </div>
              <div className="user-menu-item" onClick={handleLogout}>
                <svg width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
                  <path d="M9 21H5a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h4"></path>
                  <polyline points="16 17 21 12 16 7"></polyline>
                  <line x1="21" y1="12" x2="9" y2="12"></line>
                </svg>
                <span>Logout</span>
              </div>
            </div>
          )}
        </div>
      </div>

    </div>
  );
};

export default Topbar;

